import {useContext, useState, useEffect, useMemo} from "react";
import { UserContext } from '../Context/UserContext';
import NotPremium from "./NotPremium";
import GameBar from "./GameBar";
import BatterIconLogo from "./BatterIconLogo";
import FormattedDate from "./FormattedDate";
import "../styles/teamTrends.css"

function TeamTrends() {
    const {state, fetchTeamTrends, fetchGames, isActiveSub } = useContext(UserContext);
    const [ratingTimeframe, setRatingTimeframe] = useState(false) //false is default (last 3 seasons), true is current season only
    const [dataTimeframe, setDataTimeFrame] = useState("three_year")
    const [filteredTrends, setFilteredTrends] = useState([state.teamTrends])
    //console.log('filteredTrends', filteredTrends)
    const [sortDirection, setSortDirection] = useState(true)
    const [sortState, setSortState] = useState("Matchup Rating")

    useEffect(() => {
        if (state.games.length === 0) {
            fetchGames()
        }
        if (state.teamTrends.length === 0) {
            fetchTeamTrends()
        }
    }, [])

    useEffect(() => {
        function checkTimeFrame() {
            if (ratingTimeframe) {
                setDataTimeFrame("current_season")
            }
            else {
                setDataTimeFrame("three_year")
            }
        }

        checkTimeFrame()
    }, [ratingTimeframe])

    useEffect(() => {
        setFilteredTrends(state.teamTrends)
    }, [state, dataTimeframe])

    function handleChange() {
        setRatingTimeframe(prevTimeframe => !prevTimeframe)
    }

    function handleSort(e) {
        setSortState(e.target.dataset.sortKey)
        setSortDirection((prevDirection) => !prevDirection)
    }

    const sortedTrends = useMemo(() => {
        if (!filteredTrends || !Array.isArray(filteredTrends)) return []
        const sortedStats = [...(filteredTrends || [])]?.sort((a, b) => {
            const aValue = a?.[sortState] || "";
            const bValue = b?.[sortState] || "";

            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortDirection 
                    ? aValue.localeCompare(bValue)  // Ascending order for strings
                    : bValue.localeCompare(aValue); // Descending order for strings
            }

            const numA = Number(aValue) || 0;
            const numB = Number(bValue) || 0;
            return sortDirection ? numA - numB : numB - numA;
        });
        return sortedStats;
    }, [filteredTrends, sortState, sortDirection])

    const sampleSize = `num_games_similar_avg_matchup_rating_${dataTimeframe}`
    const three_plus_runs = `percent_three_plus_runs_${dataTimeframe}_avg_mr`
    const four_plus_runs = `percent_four_plus_runs_${dataTimeframe}_avg_mr`
    const five_plus_runs = `percent_five_plus_runs_${dataTimeframe}_avg_mr`
    const six_plus_runs = `percent_six_plus_runs_${dataTimeframe}_avg_mr`
    const seven_plus_runs = `percent_seven_plus_runs_${dataTimeframe}_avg_mr`

    return (
        <div>
            {!isActiveSub && (
                <div className="userNotPremium">
                    <NotPremium />
                </div>
            )}
            {isActiveSub && (
                <div>
                    <BatterIconLogo logoID={"pitcherRatingsBatterIconLogo"} />
                    <h2 className="teamTrendsHeader">MLB Team Total Trends for {<FormattedDate />}</h2>
                    <GameBar />
                    {/* <div className="ratings-dropdown">
                        <label>Select Rating Dataset: </label>
                        <select onChange={handleChange} value={ratingTimeframe ? "Current Season Only" : "Default (Last 3 Seasons)"}>
                            <option>Default (Last 3 Seasons)</option>
                            <option>Current Season Only</option>
                        </select>
                    </div> */}
                    <br />
                    <div className="teamInsightsNotes">
                            <li>Note: All team insights based on matchup ratings for today</li>
                            <li>Example: Scored 3+ runs with average matchup rating similar to today's</li>
                    </div>
                    {sortedTrends.length > 0 ? (
                        <div className="teamTrendsTableContainer">
                        <table className="teamTrendsTable">
                            <thead>
                                <tr>
                                    <th className={sortState === "team" ? "sorted-column" : ""} data-sort-key="team" onClick={handleSort}>Team<span>↕️</span></th>
                                    <th>Opponent</th>
                                    <th>Avg Matchup Rating</th>
                                    <th className={sortState === "sampleSize" ? "sorted-column" : ""} data-sort-key={sampleSize} onClick={handleSort}>Number Games<span>↕️</span></th>
                                    <th className={sortState === "three_plus_runs" ? "sorted-column" : ""} data-sort-key={three_plus_runs} onClick={handleSort}>3+ Runs<span>↕️</span></th>
                                    <th className={sortState === "four_plus_runs" ? "sorted-column" : ""} data-sort-key={four_plus_runs} onClick={handleSort}>4+ Runs<span>↕️</span></th>
                                    <th className={sortState === "five_plus_runs" ? "sorted-column" : ""} data-sort-key={five_plus_runs} onClick={handleSort}>5+ Runs<span>↕️</span></th>
                                    <th className={sortState === "six_plus_runs" ? "sorted-column" : ""} data-sort-key={six_plus_runs} onClick={handleSort}>6+ Runs<span>↕️</span></th>
                                    <th className={sortState === "seven_plus_runs" ? "sorted-column" : ""} data-sort-key={seven_plus_runs} onClick={handleSort}>7+ Runs<span>↕️</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTrends.map((trend) => {
                                    const relevantGame = state.games.find((game) => game.home_team === trend.team || game.away_team === trend.team)
                                    const teamStyles = {backgroundColor: state.teamColors[trend.team]?.primary || "transparent"};
                                    return (
                                        <tr key={trend.id}>
                                            <td style={teamStyles}>{trend.team}</td>
                                            {relevantGame && relevantGame?.home_team === trend.team ? (<td>{relevantGame?.away_team}</td>) : (<td>{relevantGame?.home_team}</td>)}
                                            {relevantGame && relevantGame?.home_team === trend.team ? (<td>{relevantGame?.[`home_team_${dataTimeframe}_avg_hitter_matchup_rating`]}</td>) : (<td>{relevantGame?.[`away_team_${dataTimeframe}_avg_hitter_matchup_rating`]}</td>)}
                                            {/* {relevantGame && relevantGame?.home_team === trend.team ? (<td>{relevantGame?.[`home_team_${dataTimeframe}_erh`]}</td>) : (<td>{relevantGame?.[`away_team_${dataTimeframe}_erh`]}</td>)} */}
                                            <td>{trend[`num_games_similar_avg_matchup_rating_${dataTimeframe}`]}</td>
                                            <td>{trend[`percent_three_plus_runs_${dataTimeframe}_avg_mr`]?.toFixed(0)}%</td>
                                            <td>{trend[`percent_four_plus_runs_${dataTimeframe}_avg_mr`]?.toFixed(0)}%</td>
                                            <td>{trend[`percent_five_plus_runs_${dataTimeframe}_avg_mr`]?.toFixed(0)}%</td>
                                            <td>{trend[`percent_six_plus_runs_${dataTimeframe}_avg_mr`]?.toFixed(0)}%</td>
                                            <td>{trend[`percent_seven_plus_runs_${dataTimeframe}_avg_mr`]?.toFixed(0)}%</td>
                                        </tr>
                                    )
                                    })
                                }
                            </tbody>
                        </table>
                        </div>
                    ) : (<h2 style={{textAlign: "center"}}>No Team Trends</h2>)}
                </div>
            )}
        </div>
    )
}

export default TeamTrends